import { useState } from "react";
import { Modal, Field, Input, Select, TextArea, Button } from "@/admin/ui";
import { toast } from "sonner";
import { ws, readFileAsBase64, fmtBytes } from "@/user/workspace/ws.api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-\s0-9]{7,20}$/;

export default function SubmissionForm({ form, role, editing, open, onClose, onSaved }) {
    const [values, setValues] = useState(() => ({ ...(editing?.data || {}) }));
    const [priority, setPriority] = useState(editing?.priority || "medium");
    const [tags, setTags] = useState((editing?.tags || []).join(", "));
    const [notes, setNotes] = useState(editing?.notes || "");
    const [attachments, setAttachments] = useState(editing?.attachments || []);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    if (!form) return null;

    const set = (name, v) => setValues((s) => ({ ...s, [name]: v }));

    function validate() {
        const e = {};
        for (const f of form.fields) {
            const v = values[f.name];
            if (f.required && (v === undefined || v === "" || v === null)) {
                e[f.name] = "Required";
                continue;
            }
            if (v) {
                if (f.type === "email" && !EMAIL_RE.test(v)) e[f.name] = "Invalid email";
                if (f.type === "tel" && !PHONE_RE.test(v)) e[f.name] = "Invalid phone";
                if ((f.type === "number" || f.type === "currency") && isNaN(Number(v)))
                    e[f.name] = "Must be a number";
            }
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    function buildTitle() {
        const first = form.fields.find((f) => f.required) || form.fields[0];
        const v = first ? values[first.name] : "";
        return v ? `${form.label} · ${v}` : form.label;
    }

    async function handleFile(ev) {
        const file = ev.target.files?.[0];
        ev.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const data_base64 = await readFileAsBase64(file);
            const att = await ws.uploadAttachment({
                filename: file.name,
                content_type: file.type || "application/octet-stream",
                data_base64,
                submission_id: editing?.id || "",
            });
            setAttachments((a) => [...a, att]);
            toast.success("File uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    }

    async function removeAtt(id) {
        try {
            await ws.deleteAttachment(id);
            setAttachments((a) => a.filter((x) => x.id !== id));
        } catch {
            toast.error("Could not remove file");
        }
    }

    async function save(status) {
        if (status !== "draft" && !validate()) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        setBusy(true);
        const payload = {
            type: form.key,
            title: buildTitle(),
            data: values,
            priority,
            tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            notes,
            status,
            attachment_ids: attachments.map((a) => a.id),
        };
        try {
            if (editing?.id) {
                await ws.updateSubmission(editing.id, {
                    title: payload.title, data: values, priority, tags: payload.tags,
                    notes, attachment_ids: payload.attachment_ids,
                    ...(status === "submitted" ? { status: "submitted" } : {}),
                });
            } else {
                await ws.createSubmission(payload);
            }
            toast.success(status === "draft" ? "Draft saved" : "Submission saved");
            onSaved?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={editing ? `Edit · ${form.label}` : form.label}
            size="lg"
            testid="submission-form-modal"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} data-testid="sf-cancel">Cancel</Button>
                    <Button variant="subtle" onClick={() => save("draft")} disabled={busy} data-testid="sf-save-draft">
                        Save Draft
                    </Button>
                    <Button variant="primary" onClick={() => save("submitted")} disabled={busy} data-testid="sf-submit">
                        {busy ? "Saving…" : "Submit Update"}
                    </Button>
                </>
            }
        >
            {form.desc && <p className="-mt-2 mb-5 text-[13px] text-neutral-500">{form.desc}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {form.fields.map((f) => (
                    <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                        <Field label={f.label + (f.required ? " *" : "")}>
                            <FieldInput field={f} value={values[f.name] ?? ""} onChange={(v) => set(f.name, v)} />
                        </Field>
                        {errors[f.name] && (
                            <div className="mt-1 text-[11px] text-rose-600" data-testid={`sf-err-${f.name}`}>{errors[f.name]}</div>
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Priority">
                    <Select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="sf-priority">
                        {["low", "medium", "high", "urgent"].map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </Select>
                </Field>
                <Field label="Tags (comma separated)">
                    <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="q3, priority-client" data-testid="sf-tags" />
                </Field>
            </div>

            <div className="mt-4">
                <Field label="Additional Notes">
                    <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any extra context…" data-testid="sf-notes" />
                </Field>
            </div>

            <div className="mt-4">
                <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500 mb-2">Attachments</div>
                <label className="flex items-center gap-3 rounded-xl border border-dashed border-black/15 bg-white px-4 py-3 cursor-pointer hover:border-violet-300 transition-colors" data-testid="sf-upload-label">
                    <span className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 inline-flex items-center justify-center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                    <span className="text-[13px] text-neutral-600">
                        {uploading ? "Uploading…" : "Upload PDF, Excel, Word, image, invoice…"}
                    </span>
                    <input type="file" className="hidden" onChange={handleFile} accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.txt" data-testid="sf-file-input" />
                </label>
                {attachments.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid="sf-attachments">
                        {attachments.map((a) => (
                            <li key={a.id} className="flex items-center justify-between rounded-xl bg-neutral-50 border border-black/5 px-3 py-2">
                                <span className="text-[13px] text-black truncate">{a.filename}</span>
                                <div className="flex items-center gap-3">
                                    <span className="text-[11px] text-neutral-400">{fmtBytes(a.size)}</span>
                                    <button onClick={() => removeAtt(a.id)} className="text-rose-500 hover:text-rose-600 text-[12px]" data-testid={`sf-att-remove-${a.id}`}>Remove</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Modal>
    );
}

function FieldInput({ field, value, onChange }) {
    if (field.type === "textarea") {
        return <TextArea value={value} onChange={(e) => onChange(e.target.value)} data-testid={`sf-field-${field.name}`} />;
    }
    if (field.type === "select") {
        return (
            <Select value={value} onChange={(e) => onChange(e.target.value)} data-testid={`sf-field-${field.name}`}>
                <option value="">— Select —</option>
                {(field.options || []).map((o) => (
                    <option key={o} value={o}>{o}</option>
                ))}
            </Select>
        );
    }
    const type = field.type === "currency" ? "number" : field.type === "tel" ? "tel" : field.type === "number" ? "number" : field.type === "date" ? "date" : field.type === "email" ? "email" : "text";
    return (
        <Input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || (field.type === "currency" ? "0" : "")}
            data-testid={`sf-field-${field.name}`}
        />
    );
}
